import { useEffect,useRef,useState } from 'react';

import FormError from '@/shared-components/error/components/FormError';
import CalendarIcon from '@/shared-components/icons/components/forms/CalendarIcon';
import PasswordEyeClosed from '@/shared-components/icons/components/forms/PasswordEyeClosed';
import PasswordEyeOpen from '@/shared-components/icons/components/forms/PasswordEyeOpen';
import InfoIconArtwork from '@/shared-components/icons/components/public/InfoIconArtwork';

import ChooseInput from './ChooseInput';
import FormCheckbox from './FormCheckbox';
import MultipleSelector from './MultipleSelector';
import PublicSelect from './PublicSelect';

import './styles/PublicFormInput.scss';

export default function PublicFormInput({ className=null, placeholder="", type="text", onChange=null, margin=false, element="", inputMode=null, maxLength=null, rows=null, value, fillWith, reset, error, readOnly=false, children, selectOptions, optionA="", optionAText="", optionB, optionBText="", fixedPlaceholder, secondaryColor = false, headerInfoIcon="", infoIconDescription="", headerInfoIcon2="", infoIconDescription2="", currency=null }) {
  // Only used for type password
  const [showPassword, setShowPassword] = useState(false);
  const inputRef = useRef();
  const dateRef = useRef();
  const [isInfoIconOpen, setIsInfoIconOpen] = useState(false);

  const handleInfoIconClick = () => {
    setIsInfoIconOpen(!isInfoIconOpen);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }, 0);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(()=>{
    if(reset === true) inputRef.current.value = "";
  }, [reset]);

  if(type === "checkbox") {
    return (
      <>
        <div className={`public-form-input__higher-container ${className} ${margin ? "public-form-input__default-margin" : ""}`}>
          <div className="public-form-input__checkbox-container">
            <FormCheckbox 
              className="public-form-input__checkbox" 
              element={element} 
              readOnly={readOnly} 
              value={value} 
              onChange={onChange}
            />

            <p className="public-form-input__checkbox-text element-non-selectable">
              {children}
            </p>
          </div>

          <FormError 
            error={error} 
            alternativeElement={`${element}_error`} 
            errorClassName="public-form-input__checkbox-error" 
            element={element}
          />
                    
          {margin && <div className="public-form-input__default-margin checkbox"/>}
          {className && <div className={className}></div>}
        </div>
      </>
    );
  }

  if(type === "select") {
    return (
      <>
        <div className={`public-form-input__higher-container ${className} ${margin ? "public-form-input__default-margin" : ""}`}>
          <PublicSelect 
            element={element} 
            selectOptions={selectOptions} 
            readOnly={readOnly} 
            placeholder={placeholder} 
            value={value} 
            onChange={onChange}
          />

          <FormError 
            error={error} 
            alternativeElement={`${element}_error`} 
            errorClassName="public-form-input__input-error" 
            element={element}
          />
        </div>
      </>
    );
  }

  if(type === "multiple") {
    return (
      <>
        <div className={`public-form-input__higher-container ${className} ${margin ? "public-form-input__default-margin" : ""}`}>
          <MultipleSelector 
            element={element} 
            selectOptions={selectOptions} 
            readOnly={readOnly} 
            placeholder={placeholder} 
            fillWith={fillWith}
          />

          <FormError 
            error={error} 
            alternativeElement={`${element}_error`} 
            errorClassName="public-form-input__input-error" 
            element={element}
          />
        </div>
      </>
    );
  }

  if(type === "choose") {
    return (
      <>
        <div className={`public-form-input__higher-container ${className} ${margin ? "public-form-input__default-margin" : ""}`}>
          <ChooseInput 
            optionA={optionA} 
            optionB={optionB}
            optionAText={optionAText}
            optionBText={optionBText} 
            readOnly={readOnly} 
            value={value} 
            onChange={onChange}
            headerInfoIcon={headerInfoIcon === '' ? value : headerInfoIcon}
            infoIconDescription={infoIconDescription}
            headerInfoIcon2={headerInfoIcon2}
            infoIconDescription2={infoIconDescription2}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <div className={`public-form-input__higher-container ${className} ${margin ? "public-form-input__default-margin" : ""}`}>
        <div className="public-form-input__container" id={`${element}_error`}>
          { placeholder && 
            (<label 
              htmlFor={element} 
              className={`public-form-input__label${type==="textarea" ? " textarea" : ""}`}>
              {placeholder}
              {infoIconDescription.length > 0 &&
                <InfoIconArtwork
                  className="step-accordeon-step-header__info-icon"
                  onClick={handleInfoIconClick}
                  open={isInfoIconOpen}
                  header={placeholder}
                  description={infoIconDescription}
                />
              }
            </label>)
          }
          {
            type === "textarea" ? 
              <textarea 
                className="public-form-input__input textarea" 
                name={element} 
                id={element} 
                ref={inputRef} 
                readOnly={readOnly} 
                spellCheck={false} 
                value={value} 
                defaultValue={fillWith} 
                onChange={onChange}
                rows={rows}
                maxLength={maxLength}
              />
              :
              type === "date" ?
                <>
                  <input 
                    className="public-form-input__input date" 
                    type={type} 
                    name={element} 
                    id={element} 
                    ref={dateRef} 
                    readOnly={readOnly} 
                    value={value} 
                    defaultValue={fillWith}
                    onChange={onChange}
                  />

                  <CalendarIcon className={`public-form-input__calendar-icon ${readOnly ? "read-only" : ""}`} onClick={() => dateRef.current.showPicker()}/>
                </>
                : (type === "password" && !readOnly) ?
                  <>
                    <div className={`input-container ${secondaryColor ? "secondary-color" : ""}`}>
                      {fixedPlaceholder && (
                        <span className="static-prefix">{fixedPlaceholder}</span>
                      )}
                      <input 
                        className="public-form-input__input" 
                        type={showPassword ? "text" : "password"} 
                        onChange={onChange} 
                        name={element} 
                        id={element} 
                        ref={inputRef} 
                        readOnly={readOnly} 
                        spellCheck={false} 
                        value={value} 
                        defaultValue={fillWith} 
                        inputMode={inputMode} 
                        disabled={readOnly}
                      />

                      {showPassword ? (
                        <PasswordEyeClosed className={`public-form-input__password-icon ${readOnly ? "read-only" : ""}`} onClick={togglePasswordVisibility}/>
                      ) : (
                        <PasswordEyeOpen className={`public-form-input__password-icon ${readOnly ? "read-only" : ""}`} onClick={togglePasswordVisibility}/>
                      )}
                    </div>
                  </>
                  : (type === "currencyInput" && !readOnly) ?
                    <>
                      <div className={`input-container ${secondaryColor ? "secondary-color" : ""}`}>
                        {fixedPlaceholder && (
                          <span className="static-prefix">{fixedPlaceholder}</span>
                        )}
                        <input 
                          className="public-form-input__input" 
                          type="number" 
                          onChange={onChange} 
                          name={element} 
                          id={element} 
                          ref={inputRef} 
                          readOnly={readOnly} 
                          spellCheck={false} 
                          value={value} 
                          defaultValue={fillWith} 
                          inputMode={inputMode} 
                          disabled={readOnly}
                        />

                        {currency && 
                          <div className="public-form-input__currency">
                            { currency }
                          </div>
                        }
                      </div>
                    </>
                    :
                    <>
                      <div className={`input-container ${secondaryColor ? "secondary-color" : ""}`}>
                        {fixedPlaceholder && (
                          <span className="static-prefix">{fixedPlaceholder}</span>
                        )}
                        <input 
                          className="public-form-input__input" 
                          type={type} 
                          onChange={onChange} 
                          name={element} 
                          id={element} 
                          ref={inputRef} 
                          readOnly={readOnly} 
                          spellCheck={false} 
                          value={value} 
                          defaultValue={fillWith} 
                          inputMode={inputMode} 
                          disabled={readOnly}
                        />
                      </div>
                    </>
          }
        </div>

        <FormError 
          error={error} 
          alternativeElement={`${element}_error`} 
          errorClassName="public-form-input__input-error" 
          element={element}
        />
      </div>
    </>
  );
}
